import React, {
  FC, useCallback, useMemo,
} from 'react';
import { GridItem } from '../lib/components/Grid';
import useClassifications, { SubjectDAO } from '../hooks/useSubjects';
import { GroupMultiSelectOption, MultiSelectOption, SelectOption } from '../typings/select-option.typings';
import MultiSelect from '../lib/components/MultiSelect';

export interface MultiSelectClassificationState {
  area: MultiSelectOption;
  expertise: MultiSelectOption;
  topic: MultiSelectOption;
  subtopic: MultiSelectOption;
}

export const initialMultiSelectState: MultiSelectClassificationState = {
  area: [],
  expertise: [],
  topic: [],
  subtopic: [],
};

type colRange = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

export interface SelectClassificationProps {
  disabled?: boolean;
  gridXsCols?: [colRange, colRange, colRange, colRange];
  gridSmCols?: [colRange, colRange, colRange, colRange];
  gridCols?: [colRange, colRange, colRange, colRange];
  gridLgCols?: [colRange, colRange, colRange, colRange];
  gridXlCols?: [colRange, colRange, colRange, colRange];
  gridXxlCols?: [colRange, colRange, colRange, colRange];
  values: MultiSelectClassificationState;
  onChange: (value: MultiSelectClassificationState) => void;
}

function getOptions(
  classifications: SubjectDAO,
  values: MultiSelectClassificationState,
  prop: keyof MultiSelectClassificationState,
): {
  label: string;
  options: {
    value: string;
    label: string;
  }[];
}[] {
  if (values[prop] && values[prop].length > 0) {
    return values[prop].map((item) => {
      const exps = classifications.getChildrenList(item.value);
      return ({
        label: item.label,
        options: exps.map((i) => ({
          value: i._id,
          label: i.name,
        })),
      });
    });
  }
  return [];
}

const MultiSelectClassification: FC<SelectClassificationProps> = ({
  disabled,
  gridXsCols,
  gridSmCols,
  gridCols = [3, 3, 3, 3],
  gridLgCols,
  gridXlCols,
  gridXxlCols,
  onChange,
  values,
}) => {
  const classifications = useClassifications();

  const _disabledExpertise = useMemo(() => {
    return disabled || !values.area;
  }, [disabled, values.area]);

  const _disabledTopic = useMemo(() => {
    return disabled || !values.expertise;
  }, [disabled, values.expertise]);

  const _disabledSubtopic = useMemo(() => {
    return disabled || !values.topic;
  }, [disabled, values.topic]);

  const getNewChildren = useCallback((father: string) => {
    const filter = (item: SelectOption): boolean => !classifications.isChild(item.value, father);
    const newExpertise = values.expertise?.filter(filter);
    const newTopic = values.topic?.filter(filter);
    const newSubtopic = values.subtopic?.filter(filter);
    return {
      expertise: newExpertise,
      topic: newTopic,
      subtopic: newSubtopic,
    };
  }, [classifications, values]);

  const handleArea = useCallback((value, union) => {
    switch (union.action) {
      case 'select-option':
        onChange({ ...values, area: value });
        break;
      case 'remove-value':
        if (union.removedValue) {
          const children = getNewChildren(union.removedValue.value);
          onChange({
            ...values, ...children, area: value || [],
          });
        }
        break;
      case 'clear':
        onChange({
          ...values, area: [], expertise: [], topic: [], subtopic: [],
        });
        break;
      default:
    }
  }, [onChange, values, getNewChildren]);

  const handleExpertise = useCallback((value, union) => {
    switch (union.action) {
      case 'select-option':
        onChange({ ...values, expertise: value });
        break;
      case 'remove-value':
        if (union.removedValue) {
          const children = getNewChildren(union.removedValue.value);
          onChange({
            ...values, ...children, expertise: value || [],
          });
        }
        break;
      case 'clear':
        onChange({
          ...values, expertise: [], topic: [], subtopic: [],
        });
        break;
      default:
    }
  }, [onChange, values, getNewChildren]);

  const handleTopic = useCallback((value, union) => {
    switch (union.action) {
      case 'select-option':
        onChange({ ...values, topic: value });
        break;
      case 'remove-value':
        if (union.removedValue) {
          const children = getNewChildren(union.removedValue.value);
          onChange({ ...values, ...children, topic: value || [] });
        }
        break;
      case 'clear':
        onChange({ ...values, topic: [], subtopic: [] });
        break;
      default:
    }
  }, [onChange, values, getNewChildren]);

  const handleSubtopic = useCallback((value) => {
    onChange({ ...values, subtopic: value || [] });
  }, [onChange, values]);

  const areas = useMemo((): GroupMultiSelectOption => {
    return [
      ...classifications.getList().map((c) => ({
        value: c._id,
        label: c.name,
      })),
    ];
  }, [classifications]);

  const expertises = useMemo((): GroupMultiSelectOption | [] => {
    return getOptions(classifications, values, 'area');
  }, [classifications, values]);

  const topics = useMemo((): GroupMultiSelectOption | [] => {
    return getOptions(classifications, values, 'expertise');
  }, [classifications, values]);

  const subTopics = useMemo((): GroupMultiSelectOption | [] => {
    return getOptions(classifications, values, 'topic');
  }, [classifications, values]);

  const gXs = useMemo(() => gridXsCols || gridSmCols || gridCols, [gridCols, gridSmCols, gridXsCols]);
  const gSm = useMemo(() => gridSmCols || gridCols, [gridCols, gridSmCols]);
  const gLg = useMemo(() => gridLgCols || gridCols, [gridCols, gridLgCols]);
  const gXl = useMemo(() => gridXlCols || gridLgCols || gridCols, [gridCols, gridLgCols, gridXlCols]);
  const gXxl = useMemo(() => {
    return gridXxlCols || gridXlCols || gridLgCols || gridCols;
  }, [gridCols, gridLgCols, gridXlCols, gridXxlCols]);

  return (
    <>
      <GridItem
        colXs={gXs[0]}
        colSm={gSm[0]}
        col={gridCols[0]}
        colLg={gLg[0]}
        colXl={gXl[0]}
        colXxl={gXxl[0]}
      >
        <MultiSelect
          label="Área"
          name="area"
          placeholder="Todas"
          onChange={handleArea}
          disabled={disabled}
          value={values.area}
          items={areas}
        />
      </GridItem>

      <GridItem
        colXs={gXs[1]}
        colSm={gSm[1]}
        col={gridCols[1]}
        colLg={gLg[1]}
        colXl={gXl[1]}
        colXxl={gXxl[1]}
      >
        <MultiSelect
          label="Especialidade"
          name="expertise"
          placeholder="Especialidade"
          onChange={handleExpertise}
          disabled={_disabledExpertise || values.area.length === 0}
          value={values.expertise}
          items={expertises}
        />
      </GridItem>

      <GridItem
        colXs={gXs[2]}
        colSm={gSm[2]}
        col={gridCols[2]}
        colLg={gLg[2]}
        colXl={gXl[2]}
        colXxl={gXxl[2]}
      >
        <MultiSelect
          label="Tema"
          name="topic"
          placeholder="Tema"
          onChange={handleTopic}
          disabled={_disabledTopic || values.expertise.length === 0}
          value={values.topic}
          items={topics}
        />
      </GridItem>

      <GridItem
        colXs={gXs[3]}
        colSm={gSm[3]}
        col={gridCols[3]}
        colLg={gLg[3]}
        colXl={gXl[3]}
        colXxl={gXxl[3]}
      >
        <MultiSelect
          label="Subtema"
          name="subtopic"
          placeholder="Subtema"
          onChange={handleSubtopic}
          disabled={_disabledSubtopic || values.topic.length === 0}
          value={values.subtopic}
          items={subTopics}
        />
      </GridItem>
    </>
  );
};

export default MultiSelectClassification;
